import { AddressInfo } from '@frame/ui-components/address/types'

import { IncomeSource, UploadAndClassifyFileResult } from '@documents/types'
import { PayUnit } from '@frame/components/screens/tasks/screens/stated-information/types'
import { Item } from '@frame/lib/hooks/useGetItems'
import { LoginItem } from '@script/types'

export enum DirectTaskState {
  INITIALIZED = 'initialized',
  READY = 'ready',
  PROCESSING = 'processing',
  COMPLETE = 'complete',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

export enum TrueworkJsStrategy {
  CREDENTIALS = 'credentials',
  DOCUMENTS = 'documents',
  INSTANT = 'instant',
  SMART_OUTREACH = 'smart-outreach',
  BANK = 'bank',
  EMPLOYMENT_STRATEGIES = 'employment-strategies',
  IRS = 'irs',
}

export enum EmploymentStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  NON_EMPLOYEE = 'non-employee',
}

export enum ConnectorId {
  ARGYLE = 'argyle',
  SANDBOX = 'sandbox',
  FINICITY = 'finicity',
}

export enum FinicityConnectState {
  READY = 'ready',
  COMPLETE = 'complete',
}

export type FinicityPrepData = {
  connect_url: string | null
  connect_url_expires_at: Date | null
  finicity_state: FinicityConnectState
  __cypress_behavior?: 'complete' | 'abort' // Used for Cypress tests to skip Finicity connection
}

export type ArgylePrepData = {
  api_host: string
  items: string | string[]
  plugin_key: string
  user_id: string
  user_token: string
  account_id?: string
}

type SandboxPrepData = {
  company_name: string
  company_id: string
  company_domain: string
}

export type BankIncomeStream = {
  amount: number
  income_stream_id: string
  payee: string
  posted_date: string
  transaction_id: string
}

export type StrategyInfo = {
  connector_id: ConnectorId
  prep_data: ArgylePrepData | FinicityPrepData | SandboxPrepData
  finicity_state?: FinicityConnectState
  income_streams?: BankIncomeStream[]
  has_completed_bank_attempt?: boolean
  show_employee_company_match_picker?: boolean
}

type Report = {
  start_date: Date | null
  end_date: Date | null
  is_active_employment?: boolean
  job_title: string | null
  current_gross_base_pay: number | null
  hours_worked_per_week: number | null
  pay_unit: PayUnit | null
}

type UnverifiedReport = {
  employer_name: string | null
  employer_raw_address: string | null
  employer_email: string | null
  employer_phone_number: string | null
  employer_location_place_id: string | null
} & Report

type Document = {
  id: string
  filename: string
}

export enum DocumentIncomeRequirementFileType {
  PAYSTUB = 'paystub',
  W2 = 'w2',
}

export type DocumentIncomeRequirements = {
  requirements: DocumentIncomeRequirement[]
}

type DocumentIncomeRequirement = {
  file_type: DocumentIncomeRequirementFileType
  doc_count: number | null
  lookback_period: string | null
}

type BusinessEntityMetadata = {
  logo_url?: string
}

export enum DirectTaskType {
  VERIFY_EMPLOYMENT = 'verify_employment',
  VERIFY_ASSETS = 'verify_assets',
  VERIFY_EMPLOYMENT_ASSETS = 'verify_employment_assets',
}

export type DirectTask = {
  id: string
  state: DirectTaskState
  strategy: TrueworkJsStrategy | null
  employer_name: string | null
  strategy_info: StrategyInfo | null
  report: Report | null
  unverified_report: UnverifiedReport | null
  documents: Document[]
  is_external_employment: boolean | null
  connector_company_identifier?: string | null
  task_type?: DirectTaskType | null
  isNext?: boolean
} & Partial<BusinessEntityMetadata>

export type UpdateDirectTaskBody = {
  employer_name?: string
  start_date?: Date
  end_date?: Date
  employment_status?: EmploymentStatus
  job_title?: string
  annual_income?: number
  current_gross_base_pay?: number
  employer_address?: AddressInfo
  employer_email?: string
  employer_phone_number?: string
  employer_location_place_id?: string
  strategy?: TrueworkJsStrategy
  connector_company_identifier?: string | null
  hours_worked_per_week?: number
  pay_unit?: PayUnit
}

export type CompleteDirectTaskBody = {
  strategy: TrueworkJsStrategy
  info?: {
    connector_id?: ConnectorId
    employer_name: string | null
    public_token?: string
    metadata: unknown
  }
  failed?: boolean
}

export type DirectTasksResponse = DirectTask

export type AttachFilesBody = {
  file_ids: string[]
}

type DocumentsWithOtherIncome = {
  strategy: TrueworkJsStrategy.DOCUMENTS
  income_source: IncomeSource
}

type StrategyWithEmployer = {
  strategy?: TrueworkJsStrategy
  income_source?: IncomeSource.GENERIC_INCOME
} & (
  | {
      employer_entity_id: number
    }
  | {
      employer_name: string | null
    }
)

export type CreateDirectTaskBody = {
  start_date?: Date
  end_date?: Date
  employment_status?: EmploymentStatus
  job_title?: string
  annual_income?: number
  current_gross_base_pay?: number
  employer_email?: string
  employer_phone_number?: string
  employer_location_place_id?: string
  connector_company_identifier?: string
  hours_worked_per_week?: number
  pay_unit?: PayUnit
  login_item?: LoginItem
} & (StrategyWithEmployer | DocumentsWithOtherIncome)

export type GetAllDirectTasksResponse = DirectTask[]

export type UploadFileResponse = {
  download_url: string
  file_size: number
  filename: string
  first_attached_at: string | null
  id: string
  mime_type: string
}

export enum ArgyleConnectionStatus {
  CONNECTED = 'connected',
  CONNECTING = 'connecting',
  ERROR = 'error',
}

export type ArgyleAccountResponse = {
  id?: string
  user?: string
  employers?: [string]
  item?: Item
  source?: string
  created_at?: string
  updated_at?: string
  scanned_at?: string
  connection_status?: ArgyleConnectionStatus
  connection_error_code?: string
  connection_error_message?: string
  connection_updated_at?: string
}

export type UpdateTaxInfoRequest = {
  full_name: string
  address_place_id: string
}

export type UploadFileError = { file: string[] | undefined }

export enum UploadAndClassifyResponseOutcome {
  SUCCESS = 'success',
  TIMEOUT = 'timeout',
  ERROR = 'error',
}

export type UploadAndClassifyFileResponse = {
  uploaded_file_id: string
  mime_type?: string
  reference_id?: string
  classifications: UploadAndClassifyFileResult[]
  outcome: UploadAndClassifyResponseOutcome
}
